import { useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { getSession, useSession } from 'next-auth/react';
import { buildAPIUrl } from '@/api';
import { UserContext } from '@/components/ContextProviders';
import { PageModel, UserModel } from '@/types';

// The page's user comes from getServerSideProps, fetched with the session's access
// token. After the user has been idle long enough for that token to expire, a page
// load can race (or lose) the refresh and the BE answers with an anonymous user —
// Nova then shows "Guest", no chat history and no documents even though the
// next-auth session is still alive.
//
// When that happens (session authenticated, but the page user has no identity),
// this asks next-auth for the session again — which runs the jwt callback and
// refreshes the expired token into the session cookie — then re-reads the user
// from the page-data endpoint through the /api proxy (which now forwards the
// fresh token). Only the Nova subtree consumes the result (see NovaProvider);
// nothing in the shared auth flow is changed.
export const useNovaRefreshedUser = (): UserModel => {
  const pageUser = useContext(UserContext);
  const { status } = useSession();
  const router = useRouter();
  const [refreshedUser, setRefreshedUser] = useState<UserModel | null>(null);
  const attemptedRef = useRef(false);

  const hasIdentity = !!pageUser?.userId && !!pageUser?.amsUserId;

  useEffect(() => {
    if (hasIdentity) {
      // A later navigation that loses the user again deserves its own retry.
      attemptedRef.current = false;
      setRefreshedUser(null);
      return;
    }
    if (status !== 'authenticated' || attemptedRef.current) return;
    attemptedRef.current = true;

    (async () => {
      try {
        const session = await getSession();
        // Refresh failed outright — nothing to re-read with; AuthSessionWatcher
        // takes the user back through login for this case.
        if (!session || (session as any).error) return;

        const localePrefix = router.locale && router.locale !== router.defaultLocale ? `/${router.locale}` : '';
        const resp = await fetch(`${await buildAPIUrl()}${localePrefix}`, { redirect: 'manual' });
        if (!resp.ok) return;

        const data: PageModel = await resp.json();
        if (data?.user?.userId && data.user.amsUserId) setRefreshedUser(data.user);
      } catch (error) {
        console.error('Failed to refresh Nova user details:', error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasIdentity, status]);

  return hasIdentity ? pageUser : (refreshedUser ?? pageUser);
};
